import { observable, computed, action, IObservableArray } from "mobx";

import { AppStore, AreaStore } from "./AppStore";
import { CollectionMoveWizardStore } from "./CollectionMoveWizardStore";
import { MoveDocumentWizardStore } from "./DocumentMoveWizardStore";
import { CollectionPrivilegesWizardStore } from "./CollectionPrivilegesWizardStore";
import { CollectionAsideTreeViewStore } from "./CollectionAsideTreeViewStore";
import { MoveTreeViewStore } from "./MoveTreeViewStore";
import { Urls } from "../Urls";
import { DialogStore, DialogResult } from "./DialogStore";
import { NotificationStore } from "./NotificationStore";
import { Strings } from "./StringsStore";
import * as DocumentManagement from "../DocumentManagement";
import * as Shared from "../Shared";
import { AreaUiInfoImpl } from "../States/AreaUiInfo";
import { UiRepos } from "../Stores/UiStore";
import * as Utilities from "../Utilities";

/**
 * Represents the current collection store.
 */
export class CollectionStoreImpl extends AreaStore {
	/**
  * duplicate array for storing privileges  for document
  */
	tempStorageCollectionPrivileges: DocumentManagement.CollectionPrivileges = new DocumentManagement.CollectionPrivileges();
	/**
	* Collection AreaUiInfo implementation.
	*/
	areaUiInfo: AreaUiInfoImpl = new AreaUiInfoImpl(UiRepos.COLLECTION_UI_INFO);

	/**
	* Current collection details.
	*/
	@observable collection: DocumentManagement.CollectionDetails = new DocumentManagement.CollectionDetails();

	/**
	* Documents of the current collection
	*/
	documentInfoList: IObservableArray<DocumentManagement.DocumentInfo> = observable.shallowArray([]);

	/**
	* Holds the wizard context for moving collections.
	*/
	collectionMoveWizardState: CollectionMoveWizardStore = new CollectionMoveWizardStore(this);

	/**
	* Holds the wizard context for moving documents.
	*/
	documentMoveWizardState: MoveDocumentWizardStore = new MoveDocumentWizardStore(this);

	/**
	* Holds the wizard context for adding privileges to a selected collection.
	*/
	collectionPrivilegesWizardState: CollectionPrivilegesWizardStore = new CollectionPrivilegesWizardStore(this);

	/**
	* Holds the tree view model for the AreaAside.
	*/
	asideTreeViewStore: CollectionAsideTreeViewStore = new CollectionAsideTreeViewStore(this);

	/**
	* Holds the tree view model for the move wizard dialog.
	*/
	moveTreeViewStoreCollections: MoveTreeViewStore = new MoveTreeViewStore(this, false);
	moveTreeViewStoreDocuments: MoveTreeViewStore = new MoveTreeViewStore(this, true);

	//#region Collection Tree Related properties 

	/**
	* The current search term to filter collections.
	*/
	@observable searchTerm: string = "";

	searchTimeoutHandle: number;

	/**
	* Current focused collection.
	*/
	@observable currentCollections: string = "";

	/**
	* Sets the documents section Collection-Edit component to the selection mode.
	*/
	@observable documentSelectionMode: boolean = false;

	/**
	* Sets the privileges section Collection-Edit component to the selection mode.
	*/
	@observable privilegeSelectionMode: boolean = false;

	/**
	* Sets the component to the move mode in document.
	*/
	@observable documentMoveMode: boolean = false;

	/**
	* Sets the component to the delete mode.
	*/
	@observable deleteMode: boolean = false;

	//#endregion

	//#region Collection Privilege Related properties	

	/**
	* Set the component to the add privileges mode.
	*/
	@observable addPrivilegesMode: boolean = false;


	//#endregion

	//#region Collection Tree Edit/Update and Documents Edit/Update properties  		 

	/**
	* Current added privileges
	*/
	@observable addedPrivileges: DocumentManagement.PrivilegesForGroup[] = [];
	/**
	* Holds deleted privileges for document
	*/
	removedCollectionPrivileges: DocumentManagement.PrivilegesForGroup[] = [];

	/**
	* All current selected documents items
	*/
	selectedDocuments: IObservableArray<DocumentManagement.DocumentInfo> = observable<DocumentManagement.DocumentInfo>([]);

	/**
	* All current selected privilege items
	*/
	selectedPrivileges: IObservableArray<DocumentManagement.PrivilegesForGroup> = observable<DocumentManagement.PrivilegesForGroup>([]);



	@action handleDelete = () => {
		let deleteTitle = this.asideTreeViewStore.selectedCollections.length > 1 ? Strings.documentManagement.collections.deleteMultipleCollectionsDialogTitle : Utilities.format(Strings.general.dialogDeleteTitle, this.asideTreeViewStore.selectedCollections[0].name);
		let deleteText = this.asideTreeViewStore.selectedCollections.length > 1 ? Strings.documentManagement.collections.deleteMultipleCollectionsDialogText : Utilities.format(Strings.general.dialogDeleteText, this.asideTreeViewStore.selectedCollections[0].name);
		DialogStore.show(deleteTitle, deleteText).then((result: DialogResult) => {
			if (result == DialogResult.Yes) {
				AppStore.isBusy = true;
				let removeQuery: Shared.RemoveQuery[] = this.getRemoveQueryList();
				DocumentManagement.removeCollectionsAsync(removeQuery)
					.then(() => {
						this.asideTreeViewStore.toggleSelectionMode();
						this.asideTreeViewStore.getRoots();
						AppStore.isBusy = false;
						NotificationStore.showSuccessMessage(Strings.general.deleteSuccessMessage);
					}).catch(error => {
						AppStore.isBusy = false;
						DialogStore.showError(Strings.general.errorDialogTitle, error);
					});
			}
		});
	}

	/**
   * Resets the store after a move operation.	
   */
	@action public resetMoveMode = (): void => {
		this.moveTreeViewStoreCollections.currentFocusedCollection = null;
		this.moveTreeViewStoreCollections.inMoveMode = false;
		this.moveTreeViewStoreDocuments.currentFocusedCollection = null;
		this.moveTreeViewStoreDocuments.inMoveMode = false;
		this.asideTreeViewStore.inSelectionMode = false;
		if (this.asideTreeViewStore.selectedCollections) {
			this.asideTreeViewStore.selectedCollections.clear();
		}
	}

	public getSelectionGuidList(): string[] {
		let guids: string[] = [];
		this.asideTreeViewStore.selectedCollections.forEach(collection => {
			guids.push(collection.guid);
		});
		return guids;
	}

	/**
	 * getting GUIDs of selected Documents for move operation
	 */
	public getSelectionDocumentsGuidList(): IObservableArray<string> {
		let guids: IObservableArray<string> = observable.array([]);
		this.selectedDocuments.forEach(document => {
			guids.push(document.guid);
		});
		return guids;
	}

	public getRemoveQueryList(): Shared.RemoveQuery[] {
		let removeQueryList: Shared.RemoveQuery[] = [];
		this.asideTreeViewStore.selectedCollections.forEach(collection => {
			removeQueryList.push({ guid: collection.guid, lastModified: collection.lastModified });
		});
		return removeQueryList;
	}

	/**
	* Gets if there any item is selected
	*/
	@computed get hasSelectedCollections(): boolean {
		return this.asideTreeViewStore.selectedCollections && this.asideTreeViewStore.selectedCollections.length > 0;
	}

	@computed get hasSelectedDocumentsItems(): boolean {
		return this.selectedDocuments && this.selectedDocuments.length > 0;
	}

	@computed get hasSelectedPrivilegeItems(): boolean {
		return this.selectedPrivileges && this.selectedPrivileges.length > 0;
	}

	/**
	* Toggles the selections documents mode.
	*/
	@action public toggleDocumentSelectionMode = (): void => {
		this.documentSelectionMode = !this.documentSelectionMode;
		if (!this.documentSelectionMode) {
			this.selectedDocuments.clear();
		}
	}

	/**
	* Toggles the selections privileges mode.
	*/
	@action public togglePrivilegeSelectionMode = (): void => {
		this.privilegeSelectionMode = !this.privilegeSelectionMode;
		if (!this.privilegeSelectionMode) {
			this.selectedPrivileges.clear();
		}
	}

	/**
	* Enables the move mode in Document.
	*/
	@action public enableDocumentMoveMode = (): void => {
		this.documentSelectionMode = false;
		this.documentMoveMode = true;
	}

	/**
	* Resets the store after a move operation.
	* Clears all temporary selected items for moving from the store.
	*/
	@action public resetDocumentMove = (): void => {
		this.documentMoveMode = false;
		this.documentSelectionMode = false;
	}

	public handleDocumentsDelete: () => void;
	public handlePrivilegesDelete: () => void;
	/**
	* Callback for the document move operation.
	*/
	public startDocumentMove: () => void

	/**
	* Callback for saving selected Privileges to the CollectionStore
	*/
	public saveSelectedPrivilege: () => void

	public updateImplicitPrivileges(collection: DocumentManagement.CollectionDetails) {
		let privileges: DocumentManagement.CollectionPrivileges = new DocumentManagement.CollectionPrivileges();
		privileges.collectionId = collection.privileges.collectionId;
		collection.privileges.privilegesForGroups.forEach((privilege) => {
			privileges.privilegesForGroups.push(privilege);
		});
		this.addedPrivileges.forEach((privilege) => {
			privileges.privilegesForGroups.push(privilege);
		});
		DocumentManagement.getImplicitCollectionPrivilegesAsync({ parentCollectionId: collection.parentId, privileges: privileges }).then(result => {
			this.updatePrivileges(collection.privileges.privilegesForGroups, result.privilegesForGroups);
			this.updatePrivileges(this.addedPrivileges, result.privilegesForGroups);
			result.privilegesForGroups.forEach(resultPrivilege => {
				if (!collection.privileges.privilegesForGroups.some(cp => cp.groupId === resultPrivilege.groupId) && !this.addedPrivileges.some(ap => ap.groupId === resultPrivilege.groupId)) {
					if (this.removedCollectionPrivileges.some(p => p.groupId === resultPrivilege.groupId)) {
						this.collection.privileges.privilegesForGroups.push(resultPrivilege);
						this.removedCollectionPrivileges = this.removedCollectionPrivileges.filter(rp => rp.groupId !== resultPrivilege.groupId);
					}
					else {
						this.addedPrivileges.push(resultPrivilege);

					}
				}
			});
		}).catch(error => {
			AppStore.isBusy = false;
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}

	@action updatePrivileges(privilegesForGroup: DocumentManagement.PrivilegesForGroup[], result: DocumentManagement.PrivilegesForGroup[]) {
		privilegesForGroup.forEach(p => {
			let resultPrivileges = result.filter(r => r.groupId === p.groupId);
			if (resultPrivileges.length === 0) {
				p.privilegeMemberships.forEach(m => {
					m.isExplicitMemberOf = false;
					m.isImplicitMemberOf = false;
				});
			}
			else {
				p.privilegeMemberships = resultPrivileges[0].privilegeMemberships;
			}
		});
	}
}